import { useState, useEffect, useCallback } from "react";
import { initializePaddle } from "@paddle/paddle-js";
import {
  CreditCard, CheckCircle2, XCircle, Clock,
  Zap, Users, ArrowRight, AlertCircle, RefreshCw, X
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { MARKET_TOKEN_KEY } from "@/pages/market/constants";

const API = process.env.REACT_APP_BACKEND_URL;
const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN || "";
const ACCENT = "#ff4d00";

const PLAN_META = {
  carrier_basic_monthly:  { label: "Basic",    period: "/ mo",  badge: "MARKET ACCESS",   color: "#60a5fa" },
  carrier_basic_annual:   { label: "Basic",    period: "/ yr",  badge: "MARKET ACCESS",   color: "#60a5fa" },
  carrier_pro_monthly:    { label: "Pro",      period: "/ mo",  badge: "FULL BOARD",       color: ACCENT    },
  carrier_pro_annual:     { label: "Pro",      period: "/ yr",  badge: "FULL BOARD",       color: ACCENT    },
  flex_weekly:            { label: "Flex",     period: "/ wk",  badge: "WEEKLY FLEX",      color: "#a78bfa" },
  placement_fee:          { label: "Per Hire", period: "one-time", badge: "PLACEMENT FEE", color: "#4ade80" },
};

const STATUS_COLOR = {
  active:    "#4ade80",
  canceling: "#f59e0b",
  canceled:  "#ef4444",
  paused:    "#94a3b8",
  trialing:  "#60a5fa",
  paid:      "#4ade80",
  completed: "#4ade80",
  payment_failed: "#ef4444",
};

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || "#94a3b8";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm"
      style={{ background: `${color}18`, color, border: `1px solid ${color}44` }}
    >
      {status === "active" && <CheckCircle2 className="w-2.5 h-2.5" />}
      {status === "canceling" && <Clock className="w-2.5 h-2.5" />}
      {(status === "canceled" || status === "payment_failed") && <XCircle className="w-2.5 h-2.5" />}
      {status}
    </span>
  );
}

export default function CarrierBilling({ marketUser }) {
  const token = localStorage.getItem(MARKET_TOKEN_KEY) || sessionStorage.getItem(MARKET_TOKEN_KEY) || "";
  const [paddle, setPaddle]             = useState(null);
  const [billing, setBilling]           = useState({ subscriptions: [], transactions: [], catalog: [] });
  const [loading, setLoading]           = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [cancelConfirm, setCancelConfirm] = useState(null);

  // Init Paddle.js
  useEffect(() => {
    if (!PADDLE_TOKEN) return;
    initializePaddle({ environment: "sandbox", token: PADDLE_TOKEN })
      .then(p => { if (p) setPaddle(p); })
      .catch(err => console.warn("Paddle init failed:", err));
  }, []);

  const fetchBilling = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/billing/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBilling(r.data);
    } catch {
      /* silent — carrier may have no billing yet */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBilling(); }, [fetchBilling]);

  async function openCheckout(plan) {
    if (!paddle) {
      toast.error("Paddle checkout not initialised. Please refresh and try again.");
      return;
    }
    setCheckoutLoading(plan);
    try {
      const r = await axios.post(
        `${API}/api/billing/checkout`,
        { plan, carrier_email: marketUser?.email || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { transaction_id } = r.data;
      paddle.Checkout.open({
        transactionId: transaction_id,
        settings: {
          displayMode: "overlay",
          theme: "dark",
          locale: "en",
          successUrl: `${window.location.origin}/market?billing=success`,
        },
        eventCallback: async (e) => {
          if (e.name === "checkout.completed") {
            toast.success("Payment successful! Your plan is now active.");
            setTimeout(() => fetchBilling(), 2000);
          }
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed — please try again.";
      toast.error(msg);
    } finally {
      setCheckoutLoading("");
    }
  }

  async function handleCancel(subId) {
    try {
      await axios.post(
        `${API}/api/billing/subscriptions/${subId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Subscription set to cancel at end of billing period.");
      setCancelConfirm(null);
      fetchBilling();
    } catch {
      toast.error("Cancel failed — please try again.");
    }
  }

  // Build plan list from catalog + meta
  const catalogByKey = Object.fromEntries(billing.catalog.map(c => [c.plan_key, c]));
  const activePlans = Object.entries(PLAN_META).filter(([key]) => {
    if (billingPeriod === "monthly" && key.endsWith("_annual")) return false;
    if (billingPeriod === "annual"  && key.endsWith("_monthly")) return false;
    return true;
  });

  const activeSubs = billing.subscriptions.filter(s => !["canceled"].includes(s.status));
  const hasActiveSub = activeSubs.length > 0;

  const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const fmtCents = n => n ? `$${(n / 100).toFixed(0)}` : null;

  return (
    <div className="space-y-6">
      {/* Active subscriptions */}
      {activeSubs.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Active Plans</p>
          {activeSubs.map(sub => {
            const item = sub.items?.[0] || {};
            const priceKey = Object.keys(PRICE_CATALOG_REVERSE).find(k => k === item.price?.id) || "";
            const meta = PLAN_META[priceKey] || { label: sub.id?.slice(0, 8), color: "#94a3b8", badge: "PLAN" };
            return (
              <div
                key={sub.id}
                className="flex items-start justify-between gap-3 p-4"
                style={{ background: "#111", border: "1px solid rgba(255,255,255,0.07)", borderLeft: `3px solid ${meta.color}` }}
              >
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-[0.1em]" style={{ color: meta.color }}>
                      {meta.label}
                    </span>
                    <StatusBadge status={sub.status} />
                  </div>
                  {sub.next_billed_at && (
                    <p className="text-[10px] text-white/30">
                      Next billing: {fmtDate(sub.next_billed_at)}
                    </p>
                  )}
                  {sub.scheduled_change?.action === "cancel" && (
                    <p className="text-[10px]" style={{ color: "#f59e0b" }}>
                      Cancels: {fmtDate(sub.scheduled_change.effective_at)}
                    </p>
                  )}
                </div>
                {sub.status === "active" && (
                  <button
                    onClick={() => setCancelConfirm(sub.id)}
                    className="shrink-0 text-[9px] uppercase tracking-[0.15em] px-2 py-1 transition-opacity hover:opacity-70"
                    style={{ border: "1px solid rgba(239,68,68,0.3)", color: "#ef4444" }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Period toggle */}
      <div className="flex items-center gap-1 p-1 self-start"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {["monthly", "annual"].map(p => (
          <button
            key={p}
            onClick={() => setBillingPeriod(p)}
            className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] transition-all"
            style={{
              background: billingPeriod === p ? ACCENT : "transparent",
              color: billingPeriod === p ? "#fff" : "rgba(255,255,255,0.4)",
            }}
          >
            {p}{p === "annual" && <span className="ml-1 text-[8px]" style={{ color: billingPeriod === "annual" ? "#fff" : "#4ade80" }}>SAVE 20%</span>}
          </button>
        ))}
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {activePlans.map(([planKey, meta]) => {
          const catalogEntry = catalogByKey[planKey];
          const price = fmtCents(catalogEntry?.amount_cents);
          const priceId = catalogEntry?.price_id;
          const isUnconfigured = !priceId;
          const isPro        = planKey.includes("pro");
          const isComingSoon = true; // All plans free during launch — "free lunch access" phase

          return (
            <div
              key={planKey}
              className="flex flex-col gap-4 p-5"
              data-testid={`billing-plan-${planKey}`}
              style={{
                background: isPro ? "rgba(255,77,0,0.05)" : "#111",
                border: `1px solid ${isPro ? "rgba(255,77,0,0.3)" : "rgba(255,255,255,0.07)"}`,
                borderTop: `3px solid ${meta.color}`,
              }}
            >
              {/* Plan header */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-0.5"
                    style={{ background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}44` }}>
                    {meta.badge}
                  </span>
                  {isPro && (
                    <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5"
                      style={{ background: "rgba(255,77,0,0.15)", color: ACCENT, border: `1px solid ${ACCENT}55` }}>
                      RECOMMENDED
                    </span>
                  )}
                </div>
                <p className="text-lg font-bold uppercase tracking-[0.05em] text-white">
                  {meta.label}
                </p>
                {isComingSoon ? (
                  <p className="text-xs font-semibold mt-1" style={{ color: "#4ade80" }}>Free during launch</p>
                ) : price ? (
                  <p className="text-2xl font-bold mt-1" style={{ color: meta.color }}>
                    {price}<span className="text-xs font-normal text-white/30 ml-1">{meta.period}</span>
                  </p>
                ) : (
                  <p className="text-xs text-white/30 mt-1">Contact us for pricing</p>
                )}
              </div>

              {/* Features */}
              <div className="space-y-1.5 flex-1">
                {planKey.includes("basic") && [
                  "Driver board access",
                  "Carrier bid placement",
                  "Transfer board",
                  "Driver document view",
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                    {f}
                  </div>
                ))}
                {planKey.includes("pro") && [
                  "Everything in Basic",
                  "DQ Packet download",
                  "DOT history requests",
                  "Tenstreet push (auto)",
                  "Priority driver access",
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                    {f}
                  </div>
                ))}
                {planKey === "flex_weekly" && [
                  "Weekly rolling commitment",
                  "Cancel any week",
                  "Active bid on Transfer Board",
                  "Auto-stop when driver exits",
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                    {f}
                  </div>
                ))}
                {planKey === "placement_fee" && [
                  "One-time per hire",
                  "Paid after transfer",
                  "No subscription required",
                  "Invoice provided",
                ].map(f => (
                  <div key={f} className="flex items-center gap-2 text-xs text-white/60">
                    <CheckCircle2 className="w-3 h-3 shrink-0" style={{ color: meta.color }} />
                    {f}
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button
                data-testid={`billing-checkout-${planKey}`}
                disabled={isComingSoon || !!checkoutLoading || isUnconfigured}
                onClick={() => !isComingSoon && openCheckout(planKey)}
                className="w-full flex items-center justify-center gap-2 py-3 text-xs font-bold uppercase tracking-[0.18em] transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: isPro ? ACCENT : "rgba(255,255,255,0.07)", color: isPro ? "#fff" : "rgba(255,255,255,0.7)", border: isPro ? "none" : "1px solid rgba(255,255,255,0.1)" }}
              >
                {checkoutLoading === planKey ? (
                  <RefreshCw className="w-3 h-3 animate-spin" />
                ) : isComingSoon ? (
                  <><Clock className="w-3 h-3" /> Coming Soon</>
                ) : (
                  <>
                    {isUnconfigured ? "Coming Soon" : hasActiveSub ? "Change Plan" : "Subscribe"}
                    {!isUnconfigured && <ArrowRight className="w-3 h-3" />}
                  </>
                )}
              </button>
              {isUnconfigured && !isComingSoon && (
                <p className="text-center text-[9px] text-white/25">Price not yet configured by admin</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Transaction history */}
      {billing.transactions.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-bold">Recent Transactions</p>
          {billing.transactions.slice(0, 5).map(tx => (
            <div key={tx.transaction_id}
              className="flex items-center justify-between gap-3 px-4 py-3 text-xs"
              style={{ background: "#111", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div>
                <p className="text-white/70 font-mono">{tx.transaction_id?.slice(0, 18)}…</p>
                <p className="text-[10px] text-white/30 mt-0.5">
                  {PLAN_META[tx.plan]?.label || tx.plan} · {fmtDate(tx.updated_at || tx.created_at)}
                </p>
              </div>
              <StatusBadge status={tx.status} />
            </div>
          ))}
        </div>
      )}

      {/* Paddle attribution */}
      <p className="text-[10px] text-white/20 text-center mt-4">
        Payments powered by{" "}
        <a href="https://paddle.com" target="_blank" rel="noreferrer"
          className="underline underline-offset-2 hover:text-white/40">Paddle</a>
      </p>

      {/* Cancel confirmation modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.8)" }}>
          <div className="w-full max-w-sm p-6 space-y-4"
            style={{ background: "#111", border: "1px solid rgba(239,68,68,0.3)", borderTop: "3px solid #ef4444" }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4" style={{ color: "#ef4444" }} />
                <p className="text-sm font-bold uppercase tracking-[0.1em] text-white">Cancel Subscription</p>
              </div>
              <button onClick={() => setCancelConfirm(null)}>
                <X className="w-4 h-4 text-white/40 hover:text-white" />
              </button>
            </div>
            <p className="text-sm text-white/60">
              Your plan stays active until the end of the current billing period. You won't be charged again.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelConfirm(null)}
                className="flex-1 py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-white/50 border border-white/10 hover:border-white/20 transition-colors"
              >
                Keep Plan
              </button>
              <button
                onClick={() => handleCancel(cancelConfirm)}
                className="flex-1 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-opacity hover:opacity-80"
                style={{ background: "#ef4444", color: "#fff" }}
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Internal reverse-lookup helper (not exported)
const PRICE_CATALOG_REVERSE = {};
